import React from 'react';
import { motion } from 'framer-motion';
import { Cloud, Smartphone, Server, Database, Globe, ShoppingCart } from 'lucide-react';

const servicesData = [
  {
    id: 1,
    title: 'Web Development',
    desc: 'High-performance React & Next.js web applications tailored to your business needs.',
              icon: <Globe size={32} className="text-primary" />,
    tags: ['React', 'Next.js', 'TypeScript']
  },
  {
    id: 2,
    title: 'Mobile Development',
    desc: 'Cross-platform mobile apps using Flutter and React Native for iOS and Android.',
              icon: <Smartphone size={32} className="text-primary" />,
    tags: ['Flutter', 'React Native', 'iOS/Android']
  },
  {
    id: 3,
    title: 'SaaS Platforms',
    desc: 'Scalable multi-tenant SaaS architectures built for high volume and growth.',
              icon: <Database size={32} className="text-primary" />,
    tags: ['Multi-tenant', 'AWS', 'Stripe']
  },
  {
    id: 4,
    title: 'POS & Retail',
    desc: 'Custom Point of Sale software for retail and hospitality businesses.',
              icon: <ShoppingCart size={32} className="text-primary" />,
    tags: ['Inventory', 'Billing', 'Real-time']
  },
  {
    id: 5,
    title: 'API Development',
    desc: 'Secure and robust REST & GraphQL APIs to power your ecosystem.',
              icon: <Server size={32} className="text-primary" />,
    tags: ['Node.js', 'Laravel', 'PostgreSQL', 'GraphQL']
  },
  {
    id: 6,
    title: 'Cloud & DevOps',
    desc: 'Automated CI/CD pipelines, cloud infrastructure setup, and monitoring.',
              icon: <Cloud size={32} className="text-primary" />,
    tags: ['Docker', 'Kubernetes', 'AWS']
  }
];

const Services = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="pt-24 pb-20 bg-gradient-to-b from-slate-50 to-white min-h-screen relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle, #1060BE 2px, transparent 2px)`,
          backgroundSize: '50px 50px',
          backgroundPosition: '50px 50px'
        }} />
      </div>

      <div className="max-w-content mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 mb-6 tracking-tight"
          >
            Our Services
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
          >
            End-to-end software development. From initial concept to production deployment and ongoing maintenance.
          </motion.p>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {servicesData.map((service, index) => (
            <motion.div
              key={service.id}
              variants={{
                hidden: { opacity: 0, y: 40, scale: 0.9 },
                visible: { 
                  opacity: 1, 
                  y: 0, 
                  scale: 1,
                  transition: {
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1]
                  }
                }
              }}
              whileHover={{ 
                y: -12,
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
              className="bg-gradient-to-br from-white to-slate-50 p-6 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl group space-y-4 cursor-default relative transition-all duration-300"
            >
              {/* Icon */}
              <div>
                <motion.div 
                  className="p-3 bg-primary/10 rounded-xl w-fit group-hover:bg-primary/20 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  {service.icon}
                </motion.div>
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-semibold text-slate-900 tracking-tight group-hover:text-primary transition-colors">
                {service.title}
              </h3>
              
              {/* Description */}
              <p className="text-slate-600 leading-relaxed text-sm">
                {service.desc}
              </p>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-2 pt-1">
                {service.tags.map((tag, tagIndex) => (
                  <motion.span
                    key={tag}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + tagIndex * 0.05 }}
                    whileHover={{ scale: 1.1, y: -2 }}
                    className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 text-slate-700 rounded-lg shadow-sm hover:shadow-md cursor-default hover:border-primary/30 transition-all"
                  >
                    {tag}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Services;